import { resolveRespecCost } from '@sorrem/shared';
import type { UIPartyCharacter } from '@sorrem-client/types';
import { AsyncStateMessage, CharacterAttributes, CoreStats, Icon, Panel, Separator } from '@sorrem-client/ui/components';
import classnames from "classnames";
import { useCallback, useState } from 'react';
import { AttributeRespecModal } from '../AttributeRespecModal';
import { CharacterSelector } from '../CharacterSelector';
import { MasteryDials } from '../MasteryDials';
import styles from './CharacterSummaryPanel.module.scss';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

interface CharacterSummaryPanelProps {
    player: UIPartyCharacter | null;
    isLoading: boolean;
    error: string | null;
    className?: string;
}

export function CharacterSummaryPanel({
    player,
    isLoading,
    error,
    className,
}: CharacterSummaryPanelProps): JSX.Element {
    const [showCharacterSelector, setShowCharacterSelector] = useState<boolean>(false);
    const toggleCharacterSelector = useCallback(() => {
        setShowCharacterSelector(!showCharacterSelector);
    }, [showCharacterSelector])
    const closeCharacterSelector = useCallback(() => {
        setShowCharacterSelector(false);
    }, []);
    const [isReshapeOpen, setIsReshapeOpen] = useState<boolean>(false);

    return (
        <Panel className={className}>
            <div className={styles.characterSummary}>
                <AsyncStateMessage
                    isLoading={isLoading}
                    error={error}
                    isEmpty={player === null}
                    loadingMessage="Loading character..."
                    emptyMessage="No character available."
                    className={styles.summaryNote}
                />

                {!isLoading && error === null && player !== null && (
                    <>
                        <div className={styles.summaryHeader} {...toTourTargetProps('character')}>
                            <h2 className={styles.summaryName}>{player.name}</h2>
                            <Icon
                                icon='circle-arrow-down'
                                tooltip="Select/manage characters"
                                aria-expanded={showCharacterSelector}
                                onClick={toggleCharacterSelector}
                                rotate={showCharacterSelector ? 180 : 0}
                            />
                        </div>
                        <div className={classnames(styles.characterSelectorPanel, showCharacterSelector && styles.visible)}>
                            {/* Grid rows animate 0fr -> 1fr; this inner wrapper is the clipped row. */}
                            <div className={styles.characterSelectorContent}>
                                <CharacterSelector onCharacterChosen={closeCharacterSelector} />
                            </div>
                        </div>
                        <Separator />

                        <MasteryDials />
                        <Separator />

                        <div className={styles.summaryBody} {...toTourTargetProps('attributes')}>
                            <CharacterAttributes
                                attributes={player.attributes}
                                isNoteShown={false}
                                headerAction={(
                                    <Icon
                                        icon="reshape"
                                        tooltip={`Reshape attributes (${resolveRespecCost(player.attributeRespecCount)} essence)`}
                                        onClick={() => setIsReshapeOpen(true)}
                                    />
                                )}
                            />
                            <CoreStats effectiveness={player.coreStats} isNoteShown={false} />
                        </div>
                        {isReshapeOpen && (
                            <AttributeRespecModal character={player} onClose={() => setIsReshapeOpen(false)} />
                        )}
                    </>
                )}

            </div>
        </Panel>
    );
}
